import {Component, inject, OnInit} from "@angular/core";
import {CmsApiService} from "../../../core/cms-api.service";
import {ModelStore} from "../../../core/model-store/model-store";
import {ReportUserGenerated} from "../../../core/model-store/models/report-user-generated";
import {ModelCacheArray} from "../../../core/model-store/model-cache-array";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {saveAs} from "file-saver";
import JSZip from "jszip";
import {generateReport} from "../generate-report-util";
import {OperationService} from "../../../operations/operation.service";
import {UrlQueryParam} from "../../../core/model-store/url-query-param";


@Component({
    selector: 'report-generated',
    templateUrl: './report-generated.component.html',
    styleUrls: ['./report-generated.component.scss'],
    standalone: false
})
export class ReportGeneratedComponent implements OnInit {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly modelStore: ModelStore = inject(ModelStore);
  private readonly operationService: OperationService = inject(OperationService);

  generatedReports: ModelCacheArray<ReportUserGenerated>;
  filteredReports: ReportUserGenerated[];
  selectedReports: string[] = [];
  searchText: string;

  constructor() {
    this.getPdfs = this.getPdfs.bind(this);
    this.deleteReports = this.deleteReports.bind(this);
    this.regenerateReports = this.regenerateReports.bind(this);
  }

  ngOnInit() {
    this.asyncInit().then();
  }

  private async asyncInit() {
    let timezone_offset : UrlQueryParam = new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset());

    this.generatedReports = this.modelStore.findAllModels('report_user_generated', timezone_offset);
    await this.generatedReports.loading;

    this.generatedReports.forEach(async (report) => {
      let imgUrl = await this.cms.fetchImageUrlFromDmsId(report.dmsId);
      report.thumbnailUrl = imgUrl.url;
    })

    this.filterReports()


    let reportViewTypes = this.modelStore.findAllModels('report_view_type');
    await reportViewTypes.loading;

    if (this.generatedReports.length > 0) {
      this.generatedReports.forEach(async report => {
        if (report.reportViewTypeId) {
          report.reportViewType = await this.modelStore.findModel('report_view_type', report.reportViewTypeId);
        }
      });
    }
  }

  async deleteReports() {
    if (this.selectedReports.length > 0) {
      for (const reportId of this.selectedReports) {
        let deletedModel =  this.modelStore.deleteModel('report_user_generated', reportId);
        await deletedModel.loading;
      }

      this.selectedReports = [];
      this.generatedReports = this.modelStore.findAllModels('report_user_generated', new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));
      await this.generatedReports.loading;
      this.filterReports();
    }
  }

  async regenerateReports() {
    console.log('regenerateReports: ', this.selectedReports, this.generatedReports);

    if (this.selectedReports.length === 1) {
      let generatedReport = this.modelStore.findModel('report_user_generated', this.selectedReports[0], new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));
      await generatedReport.loading;
      await generateReport(generatedReport, localStorage.getItem('primus.client.main'), this.operationService, this.cms);
    } else if (this.selectedReports.length > 1) {
      for (const reportId of this.selectedReports) {
        let generatedReport = this.modelStore.findModel('report_user_generated', reportId, new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));
        await generatedReport.loading;
        await generateReport(generatedReport, localStorage.getItem('primus.client.main'), this.operationService, this.cms);
      }
    }
  }

  async getPdfs() {
    console.log('GET PDF: ', this.selectedReports, this.generatedReports);

    console.log('report.id vs this.selectedReports[0]: ', this.selectedReports[0]);

    if (this.selectedReports.length === 1) {
      await this.downloadSinglePdf();
    } else if (this.selectedReports.length > 1) {
      await this.downloadMultiplePdfs();
    }
  }

  async downloadSinglePdf() {
    let downloadUrl = await this.cms.requestReportPdfFromDms(this.selectedReports[0]);
    if (downloadUrl.url) {
      let pdfFile = await fetch(downloadUrl.url);
      let pdfFileBlob = await pdfFile.blob();
      saveAs(pdfFileBlob, this.selectedReports[0] + '.pdf');
    }
  }

  async downloadMultiplePdfs() {
    const zip = new JSZip();

    let pdfUrls = [];
    for (const reportId of this.selectedReports) {
      let pdfUrl = await this.cms.requestReportPdfFromDms(reportId);
      pdfUrls.push(pdfUrl);
    }

    console.log('PDF URLS: ', pdfUrls);

    // Fetch each PDF file
    const filePromises = pdfUrls.map(async (downloadUrl, index) => {
      const response = await fetch(downloadUrl.url);
      const blob = await response.blob();
      zip.file(`${downloadUrl.id}.pdf`, blob);
    });

    // Wait for all files to be fetched and added to the zip
    await Promise.all(filePromises);

    // Generate the zip file as a blob
    zip.generateAsync({ type: 'blob' }).then(function (zipBlob) {
      // Use saveAs to save the generated zip file
      saveAs(zipBlob, 'multiple_reports.zip');
    });
  }

  reportSelectionChanged(event: MatCheckboxChange, templateId : string) {
    console.log('REPORT SELECTION CHANGED: ', event, templateId);
    if (event.checked && !this.selectedReports.includes(templateId)) {
      this.selectedReports.push(templateId);
    } else if (!event.checked && this.selectedReports.includes(templateId)) {
      this.selectedReports.splice(this.selectedReports.indexOf(templateId), 1);
    }
  }

  filterReports() {
    const searchTextLower = this.searchText ? this.searchText.toLowerCase() : '';

    this.filteredReports = this.generatedReports.filter(report => {
      const name = report.name ? report.name.toLowerCase() : '';
      const shortDescription = report.shortDescription ? report.shortDescription.toLowerCase() : '';
      const reportType : string = report.reportViewType?.name ? report.reportViewType.name.toLowerCase() : '';

      return report.reportStatus === 'generated' && (name.includes(searchTextLower) || shortDescription.includes(searchTextLower) || reportType.includes(searchTextLower));
    });
  }

}
